import { Button, Separator } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Code2, LayoutGrid } from "lucide-react";
import type { UsePortalSchemaReturn } from "../../hooks/usePortalSchema";
import { SnippetList } from "./SnippetList";
import { PortalSettingsEditor } from "./PortalSettingsEditor";
import { PageList } from "./PageList";
import { PageEditor } from "./PageEditor";
import { BlockList } from "./BlockList";
import { JsonSchemaEditor } from "./JsonSchemaEditor";
import { RestrictedSection } from "./RestrictedSection";
import type { EditorViewMode } from "../../pages/DashboardPage";

type SchemaEditorProps = Pick<
  UsePortalSchemaReturn,
  | "schema"
  | "selectedPageId"
  | "selectedPage"
  | "setSelectedPageId"
  | "addSnippet"
  | "updateSnippet"
  | "removeSnippet"
  | "addPage"
  | "updatePage"
  | "removePage"
  | "reorderPages"
  | "addBlock"
  | "updateBlock"
  | "removeBlock"
  | "reorderBlocks"
  | "updateSettings"
  | "importSchema"
> & {
  viewMode: EditorViewMode;
  onViewModeChange: (mode: EditorViewMode) => void;
  canEditContent: boolean;
  canEditTheming: boolean;
  canEditSnippets: boolean;
};

export const SchemaEditor: React.FC<SchemaEditorProps> = ({
  schema,
  selectedPageId,
  selectedPage,
  setSelectedPageId,
  addSnippet,
  updateSnippet,
  removeSnippet,
  addPage,
  updatePage,
  removePage,
  reorderPages,
  addBlock,
  updateBlock,
  removeBlock,
  reorderBlocks,
  updateSettings,
  importSchema,
  viewMode,
  onViewModeChange,
  canEditContent,
  canEditTheming,
  canEditSnippets,
}) => {
  const { t } = useTranslation();

  // JSON mode - full height editor with distinct styling
  if (viewMode === "json") {
    return (
      <div className="h-full flex flex-col bg-slate-900">
        {/* JSON mode banner - makes it obvious we're in JSON mode */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-400">{t("JSON Editor Mode")}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewModeChange("ui")}
            className="h-7 px-3 text-xs bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
          >
            <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />
            {t("Switch to UI Editor")}
          </Button>
        </div>
        <JsonSchemaEditor schema={schema} onApply={importSchema} className="flex-1 min-h-0" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Page list or collapsed header when editing */}
      {selectedPage && canEditContent ? (
        // Collapsed header with back button when editing a page
        // Uses px-4 py-2 to match the portal preview header height
        <div className="px-4 py-4 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setSelectedPageId(null)}
            title={t("Back to pages")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-sm font-medium truncate">{selectedPage.title}</h3>
        </div>
      ) : (
        // Full page list when no page is selected - scrollable when content overflows
        <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
          {canEditTheming ? (
            <PortalSettingsEditor
              settings={schema.settings ?? { disableLandingPage: false, disableRegistration: false }}
              onUpdateSettings={updateSettings}
            />
          ) : (
            <RestrictedSection label={t("Theming editing is restricted for your role.")} />
          )}
          <Separator />
          {canEditSnippets ? (
            <SnippetList
              snippets={schema.snippets ?? []}
              onAddSnippet={() => addSnippet()}
              onUpdateSnippet={updateSnippet}
              onRemoveSnippet={removeSnippet}
            />
          ) : (
            <RestrictedSection label={t("Snippet editing is restricted for your role.")} />
          )}
          <Separator />
          {canEditContent ? (
            <PageList
              pages={schema.pages}
              selectedPageId={selectedPageId}
              onSelectPage={setSelectedPageId}
              onAddPage={() => addPage()}
              onRemovePage={removePage}
              onReorderPages={(pageIds) => reorderPages(pageIds)}
            />
          ) : (
            <RestrictedSection label={t("Content editing is restricted for your role.")} />
          )}
        </div>
      )}

      {/* Selected page editor - only shown when a page is selected and content editing is allowed */}
      {selectedPage && canEditContent && (
        <>
          <Separator />
          <div className="flex-1 min-h-0 overflow-auto p-4">
            {/* Page settings */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">{t("Page Settings")}</h3>
              <PageEditor page={selectedPage} onUpdate={(updates) => updatePage(selectedPage.id, updates)} />
            </div>

            <Separator className="mt-4 mb-1.5" />

            {/* Block list */}
            <BlockList
              blocks={selectedPage.blocks}
              onAddBlock={(type) => addBlock(selectedPage.id, type)}
              onUpdateBlock={(blockId, updates) => updateBlock(selectedPage.id, blockId, updates)}
              onRemoveBlock={(blockId) => removeBlock(selectedPage.id, blockId)}
              onReorderBlocks={(blockIds) => reorderBlocks(selectedPage.id, blockIds)}
            />
          </div>
        </>
      )}
    </div>
  );
};
